import { GameObject, Rectangle, Vector2 } from '../../../types/game.types';
import { RenderManager } from '../../../core/RenderManager';
import { AudioManager } from '../../../core/AudioManager';

export class Vehicle implements GameObject {
  public id: string;
  public active: boolean = true;
  public position: Vector2;
  public velocity: Vector2 = { x: 0, y: 0 };
  public size: Vector2;
  
  private speed: number;
  private isLargeVehicle: boolean;
  private renderManager: RenderManager;
  private audioManager: AudioManager;
  private gameWidth: number;
  
  // Visual properties
  private color: string = '#FFFFFF';
  private width: number = 32;
  private height: number = 24;
  
  constructor(
    id: string,
    x: number,
    y: number,
    speed: number,
    isLargeVehicle: boolean,
    renderManager: RenderManager,
    audioManager: AudioManager,
    gameWidth: number
  ) {
    this.id = id;
    this.position = { x, y };
    this.speed = speed;
    this.isLargeVehicle = isLargeVehicle;
    this.renderManager = renderManager;
    this.audioManager = audioManager;
    this.gameWidth = gameWidth;
    
    this.setupVehicleProperties();
    this.size = { x: this.width, y: this.height };
  }

  private setupVehicleProperties(): void {
    if (this.isLargeVehicle) {
      // Trucks and buses
      this.width = 64; // 2 grid cells
      this.height = 28;
      this.color = this.getRandomTruckColor();
    } else {
      // Regular cars
      this.width = 32; // 1 grid cell
      this.height = 24;
      this.color = this.getRandomCarColor();
    }
  }

  private getRandomCarColor(): string {
    const carColors = [
      '#FF0000', // Red
      '#0000FF', // Blue
      '#FFFF00', // Yellow
      '#00FF00', // Green
      '#FF00FF', // Magenta
      '#FFFFFF', // White
      '#888888', // Gray
      '#800080', // Purple
    ];
    return carColors[Math.floor(Math.random() * carColors.length)];
  }

  private getRandomTruckColor(): string {
    const truckColors = [
      '#8B4513', // Brown
      '#FF4500', // Orange Red
      '#808080', // Gray
      '#000080', // Navy
      '#008000', // Dark Green
      '#800000', // Maroon
    ];
    return truckColors[Math.floor(Math.random() * truckColors.length)];
  }

  public update(deltaTime: number): void {
    // Move vehicle horizontally
    this.position.x += this.speed * deltaTime * 60; // Normalize for 60 FPS
    
    // Mark as inactive if far off screen (will be cleaned up by game)
    if (this.speed > 0 && this.position.x > this.gameWidth + 200) {
      this.active = false;
    } else if (this.speed < 0 && this.position.x < -200) {
      this.active = false;
    }
  }

  public getPosition(): Vector2 {
    return { ...this.position };
  }

  public getBounds(): Rectangle {
    return {
      x: this.position.x,
      y: this.position.y,
      width: this.width,
      height: this.height
    };
  }

  public getSpeed(): number {
    return this.speed;
  }

  public checkCollision(otherBounds: Rectangle): boolean {
    const myBounds = this.getBounds();
    
    return myBounds.x < otherBounds.x + otherBounds.width &&
           myBounds.x + myBounds.width > otherBounds.x &&
           myBounds.y < otherBounds.y + otherBounds.height &&
           myBounds.y + myBounds.height > otherBounds.y;
  }

  public render(ctx: CanvasRenderingContext2D): void {
    // Note: Offsets will be handled by the game when calling this method
    if (this.isLargeVehicle) {
      this.renderTruck(this.position.x, this.position.y);
    } else {
      this.renderCar(this.position.x, this.position.y);
    }
  }

  public renderWithOffset(ctx: CanvasRenderingContext2D, offsetX: number, offsetY: number): void {
    const renderX = offsetX + this.position.x;
    const renderY = offsetY + this.position.y;
    
    if (this.isLargeVehicle) {
      this.renderTruck(renderX, renderY);
    } else {
      this.renderCar(renderX, renderY);
    }
  }

  private renderCar(x: number, y: number): void {
    // Main body
    this.renderManager.fillRect(x + 2, y + 4, this.width - 4, this.height - 8, this.color);
    
    // Add darker outline
    this.renderManager.strokeRect(x + 2, y + 4, this.width - 4, this.height - 8, '#000000', 1);
    
    // Windows (lighter rectangle)
    const windowColor = this.lightenColor(this.color, 0.3);
    this.renderManager.fillRect(x + 6, y + 8, this.width - 12, this.height - 16, windowColor);
    
    // Headlights/Taillights
    const lightColor = this.speed > 0 ? '#FFFF00' : '#FF0000'; // Yellow for front, red for back
    const lightX = this.speed > 0 ? x + this.width - 4 : x;
    
    this.renderManager.fillRect(lightX, y + 6, 3, 4, lightColor);
    this.renderManager.fillRect(lightX, y + this.height - 10, 3, 4, lightColor);
    
    // Wheels (small black rectangles)
    this.renderManager.fillRect(x + 4, y + 2, 6, 3, '#000000');
    this.renderManager.fillRect(x + this.width - 10, y + 2, 6, 3, '#000000');
    this.renderManager.fillRect(x + 4, y + this.height - 5, 6, 3, '#000000');
    this.renderManager.fillRect(x + this.width - 10, y + this.height - 5, 6, 3, '#000000');
  }

  private renderTruck(x: number, y: number): void {
    // Truck cab (front part)
    const cabWidth = 20;
    const cabX = this.speed > 0 ? x + this.width - cabWidth : x;
    
    this.renderManager.fillRect(cabX, y + 2, cabWidth, this.height - 4, this.color);
    this.renderManager.strokeRect(cabX, y + 2, cabWidth, this.height - 4, '#000000', 1);
    
    // Truck bed/trailer
    const trailerWidth = this.width - cabWidth - 2;
    const trailerX = this.speed > 0 ? x : x + cabWidth + 2;
    
    const trailerColor = this.darkenColor(this.color, 0.2);
    this.renderManager.fillRect(trailerX, y + 4, trailerWidth, this.height - 8, trailerColor);
    this.renderManager.strokeRect(trailerX, y + 4, trailerWidth, this.height - 8, '#000000', 1);
    
    // Cab window
    const windowColor = this.lightenColor(this.color, 0.4);
    if (this.speed > 0) {
      this.renderManager.fillRect(cabX + 2, y + 6, 12, 10, windowColor);
    } else {
      this.renderManager.fillRect(cabX + 6, y + 6, 12, 10, windowColor);
    }
    
    // Headlights/Taillights
    const lightColor = this.speed > 0 ? '#FFFF00' : '#FF0000';
    const lightX = this.speed > 0 ? x + this.width - 2 : x;
    
    this.renderManager.fillRect(lightX, y + 6, 2, 5, lightColor);
    this.renderManager.fillRect(lightX, y + this.height - 11, 2, 5, lightColor);
    
    // Wheels (more wheels for trucks)
    const wheelPositions = this.isLargeVehicle ? [8, 16, 40, 48] : [8, 24];
    wheelPositions.forEach(wheelX => {
      if (wheelX < this.width - 6) {
        this.renderManager.fillRect(x + wheelX, y, 6, 4, '#000000');
        this.renderManager.fillRect(x + wheelX, y + this.height - 4, 6, 4, '#000000');
      }
    });
  }

  private lightenColor(color: string, amount: number): string {
    // Simple color lightening - convert to RGB, increase each component, convert back
    if (color.startsWith('#') && color.length === 7) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      
      const newR = Math.min(255, Math.floor(r + (255 - r) * amount));
      const newG = Math.min(255, Math.floor(g + (255 - g) * amount));
      const newB = Math.min(255, Math.floor(b + (255 - b) * amount));
      
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    return color; // Fallback to original color
  }

  private darkenColor(color: string, amount: number): string {
    // Simple color darkening - convert to RGB, decrease each component, convert back
    if (color.startsWith('#') && color.length === 7) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      
      const newR = Math.max(0, Math.floor(r * (1 - amount)));
      const newG = Math.max(0, Math.floor(g * (1 - amount)));
      const newB = Math.max(0, Math.floor(b * (1 - amount)));
      
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    }
    return color; // Fallback to original color
  }
}